import {
  Card,
  CardTitle,
  CardContent,
  CardDescription,
} from '@design-system/Card';

import {
  Carousel,
  CarouselNext,
  CarouselItem,
  CarouselContent,
  CarouselPrevious,
} from '@design-system/Carousel';

import type { JourneyEducationalContent } from '@viome/viome-base/src/usecase/Education/EducationModel';
import { cn } from '@/utils/styles';
import { useGetJourneyEducationalContentV2 } from '@viome/viome-base/src/usecase/Education/EducationV2Query';
import { NutritionPageEducationCarouselSkeleton } from './NutritionPageEducationCarouselSkeleton';
import { EventTracker } from '@/utils';

export type NutritionPageEducationCarouselProps = React.ComponentProps<'div'> & {
  'data-testid'?: string;
};

const onClickContent = (link: string, title: string) => {
  if (!link) return;
  EventTracker.trackEvent(
    'nutrition',
    `nutrition_hero_banner_${title.replace(/\s+/g, '')}`,
  );
  window.open(link, '_blank');
};

const renderEducationContent = (
  educationContent?: JourneyEducationalContent,
) => {

  if (!educationContent?.length) return null;

  const renderedEducationContent = educationContent.map((content, index) => (
    <CarouselItem
      key={`${content.title}-${index}`}
      className="max-w-[22rem] min-h-32"
      data-testid={`nutrition-educationCarousel-card-${content.title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div
        className="p-1 w-full h-full"
        onClick={() => onClickContent(content?.link, content.title)}
      >
        <Card
          variant="glass"
          className="size-full select-none hover:cursor-pointer"
        >
          <CardContent className="flex flex-col justify-between p-5 h-full">
            <CardTitle className="truncate typography-h3 text-primary !font-light">
              <abbr className="no-underline" title={content.title}>
                {content.title}
              </abbr>
            </CardTitle>

            <CardDescription className="truncate">
              {content.subtitle}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </CarouselItem>
  ));

  return renderedEducationContent;
};

export const NutritionPageEducationCarousel = ({
  className,
  'data-testid': testId,
  ...props
}: NutritionPageEducationCarouselProps) => {
  const {
    data: nutritionData,
    isLoading,
    isError,
  } = useGetJourneyEducationalContentV2('nutrition_header');

  if (isLoading) {
    return (
      <NutritionPageEducationCarouselSkeleton />
    )
  }

  if (isError || !nutritionData?.length) {
    return;
  }

  return (
    <Carousel className={cn('w-full relative', className)} data-testid={testId} {...props}>
      <CarouselContent>
        {renderEducationContent(nutritionData)}
      </CarouselContent>

      <CarouselPrevious variant="ghost" />
      <CarouselNext variant="ghost" />
    </Carousel>
  );
};
